import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { DrawnCard, TarotSpread } from '../types/tarot';

export interface ExportOptions {
  includeCardImages?: boolean;
  includeInterpretation?: boolean;
  format?: 'pdf' | 'png' | 'jpeg';
  quality?: number; // 0.1 to 1.0 for image quality
}

export interface ShareData {
  title: string;
  text: string;
  url?: string;
}

export class ExportService {
  /**
   * Export reading as PDF
   */
  static async exportToPDF(
    reading: {
      question: string;
      cards: DrawnCard[];
      spread: TarotSpread;
      interpretation?: string;
      aiInterpretation?: string;
    },
    options: ExportOptions = {}
  ): Promise<void> {
    const {
      includeInterpretation = true
    } = options;

    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;

    // Title
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Tarot Reading', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const date = new Date().toLocaleDateString();
    pdf.text(`Generated on ${date}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    // Question
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Question:', 20, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const questionLines = pdf.splitTextToSize(reading.question, pageWidth - 40);
    pdf.text(questionLines, 20, yPosition);
    yPosition += questionLines.length * 6 + 10;

    // Spread information
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Spread:', 20, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${reading.spread.name} (${reading.cards.length} cards)`, 20, yPosition);
    yPosition += 15;

    // Cards
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Cards:', 20, yPosition);
    yPosition += 10;

    for (let i = 0; i < reading.cards.length; i++) {
      const card = reading.cards[i];
      const position = reading.spread.positions[i];

      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 20;
      }

      // Card name and position
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${i + 1}. ${card.name}${card.isReversed ? ' (Reversed)' : ''}`, 20, yPosition);
      yPosition += 6;

      // Position meaning
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'italic');
      pdf.text(`Position: ${position.name}`, 25, yPosition);
      yPosition += 6;

      // Card meaning
      pdf.setFont('helvetica', 'normal');
      const meanings = card.isReversed ? card.meanings.reversed : card.meanings.upright;
      const meaningText = meanings.slice(0, 2).join(', ');
      const meaningLines = pdf.splitTextToSize(meaningText, pageWidth - 50);
      pdf.text(meaningLines, 25, yPosition);
      yPosition += meaningLines.length * 5 + 8;
    }

    // Interpretation
    if (includeInterpretation && (reading.interpretation || reading.aiInterpretation)) {
      // Check if we need a new page
      if (yPosition > pageHeight - 100) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Interpretation:', 20, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      const interpretationText = reading.aiInterpretation || reading.interpretation || '';
      const interpretationLines = pdf.splitTextToSize(interpretationText, pageWidth - 40);
      
      // Split into pages if needed
      const linesPerPage = Math.floor((pageHeight - yPosition - 20) / 5);
      let currentLine = 0;
      
      while (currentLine < interpretationLines.length) {
        const pageLinesEnd = Math.min(currentLine + linesPerPage, interpretationLines.length);
        const pageLines = interpretationLines.slice(currentLine, pageLinesEnd);
        
        pdf.text(pageLines, 20, yPosition);
        currentLine = pageLinesEnd;
        
        if (currentLine < interpretationLines.length) {
          pdf.addPage();
          yPosition = 20;
        }
      }
    }

    // Save the PDF
    const fileName = `tarot-reading-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  }

  /**
   * Export reading as image
   */
  static async exportToImage(
    elementId: string,
    options: ExportOptions = {}
  ): Promise<void> {
    const {
      format = 'png',
      quality = 0.9
    } = options;

    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Element with id "${elementId}" not found`);
    }

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#1a1a1a', // Dark background
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight
      });

      // Create download link
      const link = document.createElement('a');
      link.download = `tarot-reading-${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (format === 'png') {
        link.href = canvas.toDataURL('image/png');
      } else {
        link.href = canvas.toDataURL('image/jpeg', quality);
      }
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to image:', error);
      throw new Error('Failed to export reading as image');
    }
  }

  /**
   * Generate shareable text for reading
   */
  static generateShareText(reading: {
    question: string;
    cards: DrawnCard[];
    spread: TarotSpread;
  }): string {
    const cardNames = reading.cards.map(card => 
      `${card.name}${card.isReversed ? ' (R)' : ''}`
    ).join(', ');

    return `🔮 My Tarot Reading 🔮

Question: ${reading.question}

Spread: ${reading.spread.name}
Cards: ${cardNames}

Generated with Tarot Card Reader PWA`;
  }

  /**
   * Share reading using Web Share API or fallback
   */
  static async shareReading(
    reading: {
      question: string;
      cards: DrawnCard[];
      spread: TarotSpread;
    },
    permalink?: string
  ): Promise<void> {
    const shareData: ShareData = {
      title: '🔮 My Tarot Reading',
      text: this.generateShareText(reading),
      url: permalink
    };

    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          this.fallbackShare(shareData);
        }
      }
    } else {
      this.fallbackShare(shareData);
    }
  }

  /**
   * Fallback share method using clipboard
   */
  private static async fallbackShare(shareData: ShareData): Promise<void> {
    const shareText = `${shareData.title}\n\n${shareData.text}${shareData.url ? `\n\n${shareData.url}` : ''}`;
    
    try {
      await navigator.clipboard.writeText(shareText);
      // You could show a toast notification here
      console.log('Reading copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback to text selection
      this.selectText(shareText);
    }
  }

  /**
   * Select text for manual copying
   */
  private static selectText(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      console.log('Text selected for copying');
    } catch (error) {
      console.error('Failed to select text:', error);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  /**
   * Generate permalink for reading
   */
  static generatePermalink(readingId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/reading/${readingId}`;
  }

  /**
   * Create reading permalink with encoded data
   */
  static createReadingPermalink(reading: {
    question: string;
    cards: DrawnCard[];
    spread: TarotSpread;
  }): string {
    const readingData = {
      q: reading.question,
      s: reading.spread.id,
      c: reading.cards.map(card => ({
        id: card.id,
        pos: card.position,
        rev: card.isReversed
      }))
    };

    const encodedData = btoa(JSON.stringify(readingData));
    const baseUrl = window.location.origin;
    return `${baseUrl}/shared-reading?data=${encodedData}`;
  }

  /**
   * Decode permalink data
   */
  static decodePermalinkData(encodedData: string): {
    question: string;
    spreadId: string;
    cards: Array<{
      id: string;
      position: number;
      isReversed: boolean;
    }>;
  } | null {
    try {
      const decodedData = JSON.parse(atob(encodedData));
      return {
        question: decodedData.q,
        spreadId: decodedData.s,
        cards: decodedData.c
      };
    } catch (error) {
      console.error('Failed to decode permalink data:', error);
      return null;
    }
  }
}